import { useQueryClient } from '@tanstack/react-query'
import { Button, notification } from 'antd'
import BigNumber from 'bignumber.js'
import Numeral from 'numeral'
import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router'
import { parseEther } from 'viem'
import {
  useConnect,
  useAccount,
  useWriteContract,
  useChainId,
  useWaitForTransactionReceipt,
} from 'wagmi'
import { injected } from 'wagmi/connectors'

import BackImg from '@/assets/back.png'
import BackImg_Light from '@/assets/back_light.png'
import error_icon from '@/assets/error_icon.png'
import success_icon from '@/assets/success_icon.png'
import ToolTipCus from '@/components/Tooltips'
import { config, PoSDataToolTip } from '@/config'
import { useLocalStorage } from '@/context/localstorage-context'
import { useQueryPosStakeInfo } from '@/utils/queries'

import DelegateConfirm from './delegate-confirm'
import DelegateModal from './delegate-modal'
import { useDelegatorStakeInfo, useDelegatorTotalIncentive } from './pos-data'
import {
  BackBtn,
  BackBtnContainer,
  Btn,
  BtnArea,
  Data,
  Data1,
  DataTitle,
  DetailAddr,
  DetailAddr1,
  DetailAddr2,
  DetailInfo,
  Icon,
  InfoRow,
  LineCloumn,
  LineRow,
  Link,
  MainBody,
  MyActions,
  MyDelegate,
  TextGradient,
} from './pos-detail.module'

export default function PosDetail() {
  const chainId = useChainId()
  const location = useLocation()
  const [showModal, setShowModal] = useState(false)
  const [showTopUpModal, setShowTopUpModal] = useState(false)
  const [showWithdrawConfirmModal, setShowWithdrawConfirmModal] =
    useState(false)
  const { data: posStakeInfo } = useQueryPosStakeInfo()
  const delegatorStakeInfo = useDelegatorStakeInfo()
  const delegatorTotalIncentive = useDelegatorTotalIncentive()
  const { status } = useAccount()
  const { connect } = useConnect()
  const queryClient = useQueryClient()
  const { theme } = useLocalStorage()
  const { data: hash, writeContract } = useWriteContract()
  const { isSuccess: isConfirmed } = useWaitForTransactionReceipt({
    hash,
  })
  const isdark = useMemo(() => theme === 'dark', [theme])
  const PosContractConfig = config[chainId]['PosContract']

  const stakerInfo = useMemo(() => {
    if (
      posStakeInfo &&
      posStakeInfo.stakers &&
      posStakeInfo.stakers.length > 0
    ) {
      const staker = posStakeInfo.stakers.find(
        (v: any) => v.address === location.pathname.split('/posdetail/')[1],
      )
      if (staker) {
        staker.myDelegate = '0'
        staker.myTotalIncentive = '0'
        delegatorStakeInfo.forEach((j: any) => {
          if (staker.address.toLowerCase() === j.address.toLowerCase()) {
            staker.myDelegate = j.amount.toString()
          }
        })
        delegatorTotalIncentive.forEach((t: any) => {
          if (staker.address.toLowerCase() === t.address.toLowerCase()) {
            staker.myTotalIncentive = new BigNumber(staker.myTotalIncentive)
              .plus(t.amount)
              .toString()
          }
        })
      } else {
        return []
      }
      return staker || {}
    } else {
      return {}
    }
  }, [
    delegatorStakeInfo,
    delegatorTotalIncentive,
    location.pathname,
    posStakeInfo,
  ])

  useEffect(() => {
    if (hash && isConfirmed) {
      notification.success({
        message: 'Transaction completed',
        description: <Link txHash={hash} />,
        className: `${isdark ? 'noticeSuccess_dark' : 'noticeSuccess_light'}`,
        icon: <img width={30} src={success_icon} alt="notification" />,
      })
      queryClient.invalidateQueries({
        queryKey: ['getDelegatorStakeInfo'],
      })
      setShowTopUpModal(false)
      setShowWithdrawConfirmModal(false)
    }
  }, [isConfirmed, hash])

  const posDelegateIn = (amount: string) =>
    writeContract(
      {
        address: PosContractConfig.addr,
        abi: PosContractConfig.abi,
        functionName: 'delegateIn',
        args: [stakerInfo.address],
        value: parseEther(amount),
      },
      {
        onError: () => {
          notification.error({
            message: 'Transaction Sent Failed',
            description: <Link txHash={hash || ''} />,
            className: `${isdark ? 'noticeSuccess_dark' : 'noticeSuccess_light'}`,
            icon: <img width={30} src={error_icon} alt="notification" />,
          })
          setShowTopUpModal(false)
        },
      },
    )

  const posWithdraw = () =>
    writeContract(
      {
        address: PosContractConfig.addr,
        abi: PosContractConfig.abi,
        functionName: 'delegateOut',
        args: [stakerInfo.address],
      },
      {
        onError: () => {
          notification.error({
            message: 'Transaction Sent Failed',
            description: <Link txHash={hash || ''} />,
            className: `${
              isdark ? 'noticeSuccess_dark' : 'noticeSuccess_light'
            }`,
            icon: <img width={30} src={error_icon} alt="notification" />,
          })
          setShowWithdrawConfirmModal(false)
        },
      },
    )

  const handleTopUp = async () => {
    if (status === 'connected') {
      setShowTopUpModal(true)
    } else {
      connect({ connector: injected() })
      setShowTopUpModal(true)
    }
  }

  const handleWithdraw = async () => {
    if (status === 'connected') {
      setShowWithdrawConfirmModal(true)
    } else {
      connect({ connector: injected() })
      setShowWithdrawConfirmModal(true)
    }
  }

  const feeRate = useMemo(() => {
    let tmp
    try {
      tmp = Numeral(stakerInfo.feeRateFmt)
        .multiply(100)
        .format('0,0.00')
        .replace(/\.?0+$/, '')
    } catch {
      tmp = stakerInfo.feeRateFmt
    }
    return tmp
  }, [stakerInfo.feeRateFmt])

  const MaxFeeRate = useMemo(() => {
    let tmp
    try {
      tmp = Numeral(stakerInfo.maxFeeFmt)
        .multiply(100)
        .format('0,0.00')
        .replace(/\.?0+$/, '')
    } catch {
      tmp = stakerInfo.maxFeeFmt
    }
    return tmp
  }, [stakerInfo.maxFeeFmt])

  return (
    <MainBody>
      <BackBtnContainer>
        <BackBtn onClick={() => window.history.back()}>
          <div
            className="bg-white dark:bg-common-1"
            style={{ width: '32px', borderRadius: '5px' }}
          >
            <img src={isdark ? BackImg : BackImg_Light} alt="back" width={32} />
          </div>
        </BackBtn>
      </BackBtnContainer>
      <DetailAddr className="bg-white dark:bg-dark-222">
        <DetailAddr1>
          <Icon
            src={`data:image/${stakerInfo.iconType};base64,${stakerInfo.iconData}`}
            alt="icon"
          />
        </DetailAddr1>
        <DetailAddr2>
          <p
            className="text-light-0 dark:text-white"
            style={{
              fontSize: '24px',
              wordBreak: 'break-word',
              textAlign: 'left',
            }}
          >
            {stakerInfo.name}
          </p>
          <p
            className="text-light-333 dark:text-dark-0"
            style={{
              color: '#757575',
              wordBreak: 'break-word',
              textAlign: 'left',
            }}
          >
            {stakerInfo.address}
          </p>
        </DetailAddr2>
      </DetailAddr>
      <DetailInfo className="bg-white dark:bg-dark-222">
        {/********* / Part1  *********/}
        <InfoRow>
          <Data>
            <DataTitle className="text-light-444 dark:text-dark-0">
              Total Staked Amount
              <ToolTipCus title={PoSDataToolTip[0]} />
            </DataTitle>
            <p>
              <span className="text-light-0 dark:text-white">
                {stakerInfo.totalAmountFmt}
              </span>
              <span>WAN</span>
            </p>
          </Data>
          <LineCloumn isdark={isdark} />
          <Data>
            <DataTitle className="text-light-444 dark:text-dark-0">
              Remaining Quota
              <ToolTipCus title={PoSDataToolTip[1]} />
            </DataTitle>
            <p>
              <span className="text-light-0 dark:text-white">
                {stakerInfo.quotaLeft}
              </span>
              <span>WAN</span>
            </p>
          </Data>
        </InfoRow>
        <LineRow isdark={isdark} />

        {/********* / Part2  *********/}
        <InfoRow>
          <Data>
            <DataTitle className="text-light-444 dark:text-dark-0">
              Activity
              <ToolTipCus title={PoSDataToolTip[2]} />
            </DataTitle>
            <p>
              <span className="text-light-0 dark:text-white">
                {stakerInfo.activity}
              </span>
            </p>
          </Data>
          <LineCloumn isdark={isdark} />
          <Data>
            <DataTitle className="text-light-444 dark:text-dark-0">
              Lock Period
              <ToolTipCus title={PoSDataToolTip[3]} />
            </DataTitle>
            <p>
              <span className="text-light-0 dark:text-white">
                {stakerInfo.lockEpochs}
              </span>
              <span>{stakerInfo.lockEpochs > 1 ? 'DAYS' : 'DAY'}</span>
            </p>
          </Data>
        </InfoRow>
        <LineRow isdark={isdark} />

        {/********* / Part3  *********/}
        <InfoRow>
          <Data>
            <DataTitle className="text-light-444 dark:text-dark-0">
              Delegation Fee (%)
              <ToolTipCus title={PoSDataToolTip[4]} />
            </DataTitle>
            <p>
              <span className="text-light-0 dark:text-white">{feeRate}</span>
            </p>
          </Data>
          <LineCloumn isdark={isdark} />
          <Data>
            <DataTitle className="text-light-444 dark:text-dark-0">
              Max Delegation Fee(%)
              <ToolTipCus title={PoSDataToolTip[5]} />
            </DataTitle>
            <p>
              <span className="text-light-0 dark:text-white">{MaxFeeRate}</span>
            </p>
          </Data>
        </InfoRow>
        <LineRow isdark={isdark} />

        {/********* / Part4  *********/}
        <InfoRow>
          <Data>
            <DataTitle className="text-light-444 dark:text-dark-0">
              Status
              <ToolTipCus title={PoSDataToolTip[6]} />
            </DataTitle>
            <p>
              <span className="text-light-0 dark:text-white">
                {stakerInfo.currentStatus}
              </span>
            </p>
          </Data>
          <LineCloumn isdark={isdark} />
          <Data>
            <DataTitle className="text-light-444 dark:text-dark-0">
              Ends In
              <ToolTipCus title={PoSDataToolTip[7]} />
            </DataTitle>
            <p>
              <span className="text-light-0 dark:text-white">
                {stakerInfo.nextStatus}
              </span>
            </p>
          </Data>
        </InfoRow>
      </DetailInfo>
      {new BigNumber(stakerInfo.myDelegate).eq('0') ? (
        <Btn>
          <Button block onClick={handleTopUp}>
            Delegate
          </Button>
        </Btn>
      ) : (
        <MyDelegate className="bg-white dark:bg-dark-222">
          <InfoRow count={2} direction="column">
            <Data1>
              <DataTitle className="text-light-444 dark:text-dark-0">
                My Rewards
              </DataTitle>
              <div>
                <span className="text-light-111 dark:text-white">
                  {Numeral(stakerInfo.myTotalIncentive)
                    .format('0,0.0000')
                    .replace(/\.?0+$/, '')}
                </span>
                <span className="text-light-444 dark:text-dark-0">WAN</span>
              </div>
            </Data1>
          </InfoRow>
          <LineRow isdark={isdark} />
          <InfoRow count={2} direction="column">
            <Data1>
              <DataTitle className="text-light-444 dark:text-dark-0">
                My Delegations
              </DataTitle>
              <MyActions>
                <div>
                  <span className="text-light-111 dark:text-white">
                    {Numeral(stakerInfo.myDelegate)
                      .format('0,0.0000')
                      .replace(/\.?0+$/, '')}
                  </span>
                  <span className="text-light-444 dark:text-dark-0">WAN</span>
                </div>
                <BtnArea isdark={isdark}>
                  <Button onClick={handleTopUp}>Top-up</Button>
                  <Button onClick={handleWithdraw}>
                    <TextGradient>Undelegate</TextGradient>
                  </Button>
                </BtnArea>
              </MyActions>
            </Data1>
          </InfoRow>
        </MyDelegate>
      )}
      {showModal && (
        <DelegateModal
          type="Delegate"
          visible={showModal}
          onCancel={() => setShowModal(false)}
          stakerInfo={stakerInfo}
          action={() => {}}
        />
      )}
      {showTopUpModal && (
        <DelegateModal
          type={
            new BigNumber(stakerInfo.myDelegate).eq('0') ? 'Delegate' : 'Top-up'
          }
          visible={showTopUpModal}
          onCancel={() => setShowTopUpModal(false)}
          stakerInfo={stakerInfo}
          action={(v: string) => posDelegateIn(v)}
        />
      )}
      {showWithdrawConfirmModal && (
        <DelegateConfirm
          visible={showWithdrawConfirmModal}
          onCancel={() => setShowWithdrawConfirmModal(false)}
          stakerInfo={stakerInfo}
          action={posWithdraw}
          amount={stakerInfo.myDelegate}
          type="Undelegate WAN"
        />
      )}
    </MainBody>
  )
}
